import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './SchoolActivities.css';
import { supabase } from '../../lib/supabase';

const SchoolActivities = () => {
    const navigate = useNavigate();
    const [activities, setActivities] = useState([]);
    const [activitiesLoading, setActivitiesLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const [showClearMenu, setShowClearMenu] = useState(false);
    const [clearOption, setClearOption] = useState(null);
    const [customDate, setCustomDate] = useState('');
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [showDeleteItemConfirm, setShowDeleteItemConfirm] = useState(false);

    const observerRef = useRef(null);
    const loadMoreRef = useRef(null);
    const PAGE_SIZE = 50;

    const getSchoolId = () => localStorage.getItem('school_id');

    // Initial fetch
    useEffect(() => {
        fetchActivities(true);
    }, []);

    // Close clear menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showClearMenu && !event.target.closest('.school-activities-clear-wrapper')) {
                setShowClearMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showClearMenu]);

    // Infinite scroll observer
    useEffect(() => {
        if (activitiesLoading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    fetchActivities(false);
                }
            },
            { threshold: 0.1 }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        observerRef.current = observer;

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [activitiesLoading, hasMore, loadingMore, activities.length]);

    const fetchActivities = async (isInitial = false) => {
        const schoolId = getSchoolId();
        if (!schoolId) {
            navigate('/mydashboard');
            return;
        }

        try {
            if (isInitial) {
                setActivitiesLoading(true);
                setActivities([]);
            } else {
                setLoadingMore(true);
            }

            const offset = isInitial ? 0 : activities.length;

            const { data, error } = await supabase
                .from('cms_activities')
                .select('*')
                .eq('school_id', schoolId)
                .order('added_at', { ascending: false })
                .range(offset, offset + PAGE_SIZE - 1);

            if (error) {} else {
                if (isInitial) {
                    setActivities(data || []);
                } else {
                    setActivities(prev => [...prev, ...(data || [])]);
                }
                setHasMore((data || []).length === PAGE_SIZE);
            }
        } catch (err) {
            } finally {
            setActivitiesLoading(false);
            setLoadingMore(false);
        }
    };

    const handleClearActivities = async (option = 'all') => {
        const schoolId = getSchoolId();
        if (!schoolId) return;

        try {
            let query = supabase.from('cms_activities').delete().eq('school_id', schoolId);

            if (option === 'today') {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                query = query.gte('added_at', today.toISOString());
            } else if (option === 'week') {
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                weekAgo.setHours(0, 0, 0, 0);
                query = query.gte('added_at', weekAgo.toISOString());
            } else if (option === 'month') {
                const monthAgo = new Date();
                monthAgo.setMonth(monthAgo.getMonth() - 1);
                monthAgo.setHours(0, 0, 0, 0);
                query = query.gte('added_at', monthAgo.toISOString());
            } else if (option === 'custom' && customDate) {
                const customDateObj = new Date(customDate);
                customDateObj.setHours(0, 0, 0, 0);
                query = query.gte('added_at', customDateObj.toISOString());
            }

            const { error } = await query;

            if (error) {alert('Error clearing activities: ' + error.message);
            } else {
                await fetchActivities(true);
                setShowClearConfirm(false);
                setShowClearMenu(false);
                setClearOption(null);
                setCustomDate('');
                setShowCustomDatePicker(false);
            }
        } catch (err) {alert('Error clearing activities: ' + err.message);
        }
    };

    const handleDeleteActivity = async (activityId) => {
        try {
            setActivities(prev => prev.filter(activity => activity.id !== activityId));

            const { error } = await supabase
                .from('cms_activities')
                .delete()
                .eq('id', activityId);

            if (error) {await fetchActivities(true);
                alert('Error deleting activity: ' + error.message);
            }

            setShowDeleteItemConfirm(false);
            setDeleteItemId(null);
        } catch (err) {await fetchActivities(true);
            alert('Error deleting activity: ' + err.message);
            setShowDeleteItemConfirm(false);
            setDeleteItemId(null);
        }
    };

    const handleClearOptionClick = (option) => {
        setShowClearMenu(false);
        if (option === 'custom') {
            setClearOption('custom');
            setShowCustomDatePicker(true);
            setShowClearConfirm(true);
        } else {
            setClearOption(option);
            setShowCustomDatePicker(false);
            setShowClearConfirm(true);
        }
    };

    const formatTimeAgo = (timestamp) => {
        const now = new Date();
        const activityTime = new Date(timestamp);
        const diffInSeconds = Math.floor((now - activityTime) / 1000);

        if (diffInSeconds < 60) {
            return 'Just now';
        } else if (diffInSeconds < 3600) {
            const minutes = Math.floor(diffInSeconds / 60);
            return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
        } else if (diffInSeconds < 86400) {
            const hours = Math.floor(diffInSeconds / 3600);
            return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        } else {
            const days = Math.floor(diffInSeconds / 86400);
            return `${days} day${days > 1 ? 's' : ''} ago`;
        }
    };

    const getActivityIcon = (title) => {
        if (title.toLowerCase().includes('student')) {
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" />
                </svg>
            );
        } else if (title.toLowerCase().includes('teacher')) {
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H19V9Z" />
                </svg>
            );
        } else {
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
                </svg>
            );
        }
    };

    const getClearOptionLabel = () => {
        switch (clearOption) {
            case 'today': return 'today\'s activities';
            case 'week': return 'activities from the last week';
            case 'month': return 'activities from the last month';
            case 'custom': return `activities from ${customDate}`;
            case 'all': return 'all activities';
            default: return 'activities';
        }
    };

    return (
        <div className="school-activities-page">
            <div className="school-activities-container">
                {/* Header */}
                <div className="school-activities-header">
                    <div className="school-activities-header-left">
                        <button className="school-activities-back-btn" onClick={() => navigate('/mydashboard')}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                            </svg>
                            Back to Dashboard
                        </button>
                    </div>
                    <div className="school-activities-clear-wrapper" style={{ position: 'relative' }}>
                        <button
                            className="school-activities-clear-btn"
                            onClick={() => setShowClearMenu(!showClearMenu)}
                            disabled={activities.length === 0}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                            </svg>
                            Clear
                        </button>
                        {showClearMenu && (
                            <div className="school-activities-clear-menu">
                                <button onClick={() => handleClearOptionClick('today')}>Clear All from Today</button>
                                <button onClick={() => handleClearOptionClick('week')}>Clear All from last week</button>
                                <button onClick={() => handleClearOptionClick('month')}>Clear All from last month</button>
                                <button onClick={() => handleClearOptionClick('custom')}>Custom Date</button>
                                <button onClick={() => handleClearOptionClick('all')}>Clear All</button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Activity List */}
                <div className="school-activities-content">
                    {activitiesLoading ? (
                        <div className="school-activities-loading">
                            <div className="school-activities-spinner">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
                                </svg>
                            </div>
                            <p>Loading activities...</p>
                        </div>
                    ) : activities.length === 0 ? (
                        <div className="school-activities-empty">
                            <div className="school-activities-empty-icon">
                                <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                                </svg>
                            </div>
                            <h3>No Activities</h3>
                            <p>Activities will appear here when you add students or teachers.</p>
                        </div>
                    ) : (
                        <div className="school-activities-list">
                            {activities.map((activity) => (
                                <div key={activity.id} className="school-activities-item">
                                    <div className="school-activities-item-icon">
                                        {getActivityIcon(activity.title)}
                                    </div>
                                    <div className="school-activities-item-content">
                                        <p><strong>{activity.title}</strong></p>
                                        {activity.details && <p className="school-activities-item-details">{activity.details}</p>}
                                        <span className="school-activities-item-time">{formatTimeAgo(activity.added_at)}</span>
                                    </div>
                                    <button
                                        className="school-activities-item-delete-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteItemId(activity.id);
                                            setShowDeleteItemConfirm(true);
                                        }}
                                        title="Delete activity"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                                        </svg>
                                    </button>
                                </div>
                            ))}

                            {/* Load more trigger */}
                            <div ref={loadMoreRef} className="school-activities-load-more">
                                {loadingMore && (
                                    <div className="school-activities-loading-more">
                                        <div className="school-activities-spinner small">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
                                            </svg>
                                        </div>
                                        <span>Loading more...</span>
                                    </div>
                                )}
                                {!hasMore && activities.length > 0 && (
                                    <div className="school-activities-end">
                                        <span>No more activities to load</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Clear Confirmation Modal */}
            {showClearConfirm && (
                <div className="school-activities-modal-overlay" onClick={() => setShowClearConfirm(false)}>
                    <div className="school-activities-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="school-activities-confirmation-header">
                            <div className="school-activities-confirmation-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                                </svg>
                            </div>
                            <h2>Clear Activities</h2>
                        </div>
                        <div className="school-activities-confirmation-content">
                            {showCustomDatePicker ? (
                                <div className="school-activities-custom-date-container">
                                    <p className="school-activities-confirmation-message">Select a date to clear all activities from:</p>
                                    <input
                                        type="date"
                                        className="school-activities-custom-date-input"
                                        value={customDate}
                                        onChange={(e) => setCustomDate(e.target.value)}
                                    />
                                </div>
                            ) : (
                                <p className="school-activities-confirmation-message">
                                    Are you sure you want to clear {getClearOptionLabel()}? This action cannot be undone.
                                </p>
                            )}
                        </div>
                        <div className="school-activities-confirmation-actions">
                            <button
                                className="school-activities-cancel-btn"
                                onClick={() => {
                                    setShowClearConfirm(false);
                                    setClearOption(null);
                                    setCustomDate('');
                                    setShowCustomDatePicker(false);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="school-activities-confirm-btn"
                                onClick={() => handleClearActivities(clearOption)}
                                disabled={showCustomDatePicker && !customDate}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Single Item Confirmation */}
            {showDeleteItemConfirm && (
                <div className="school-activities-modal-overlay" onClick={() => setShowDeleteItemConfirm(false)}>
                    <div className="school-activities-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="school-activities-confirmation-header">
                            <div className="school-activities-confirmation-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                                </svg>
                            </div>
                            <h2>Delete Activity</h2>
                        </div>
                        <div className="school-activities-confirmation-content">
                            <p className="school-activities-confirmation-message">
                                Are you sure you want to delete this activity? This action cannot be undone.
                            </p>
                        </div>
                        <div className="school-activities-confirmation-actions">
                            <button
                                className="school-activities-cancel-btn"
                                onClick={() => {
                                    setShowDeleteItemConfirm(false);
                                    setDeleteItemId(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="school-activities-confirm-btn"
                                onClick={() => handleDeleteActivity(deleteItemId)}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SchoolActivities;
